import { useMemo, useRef, useState } from 'react';
import { Box, Skeleton, Typography, lighten } from '@mui/material';
import PieChart, { Connector, Font, Label, Legend, Series, Title as PieTitle, Tooltip } from 'devextreme-react/pie-chart';
import Chart, { Font as ChartFont, Label as ChartLabel, Legend as ChartLegend, Series as ChartSeries, Title as ChartTitle, Tooltip as ChartTooltip, ValueAxis } from 'devextreme-react/chart';
import { DashboardCard } from '../components/DashboardCard';
import { DashboardChartViewSelect } from '../components/DashboardChartViewSelect';
import { useStudentServiceData } from '../contexts';
import { StudentServiceFilterControls } from './StudentServiceFilterControls';
import { StudentServiceComparisonChart } from './StudentServiceComparisonChart';
import { DASHBOARD_CHART_VIEW_CONFIG, DASHBOARD_DISTRIBUTION_COLORS, DashboardChartView, getDashboardDistributionColor } from '../types';

export const StudentServiceChartCard = () => {
    const { data, loading, error, filter, metric, totalsByYear } = useStudentServiceData();
    const chartRef = useRef<PieChart>(null);
    const cartesianChartRef = useRef<Chart>(null);
    const [chartView, setChartView] = useState<DashboardChartView>(DASHBOARD_CHART_VIEW_CONFIG.studentServices.recommendedView);
    const rows = useMemo(() => {
        const map = new Map<string, { name: string; participants: number; totalHours: number; order: number }>();
        data.forEach(item => {
            const existing = map.get(item.studentServiceName);
            if (existing) { existing.participants += item.studentCount; existing.totalHours += item.totalHours; }
            else map.set(item.studentServiceName, { name: item.studentServiceName, participants: item.studentCount, totalHours: item.totalHours, order: item.studentServiceDisplayOrder });
        });
        const enrolled = totalsByYear.reduce((sum, year) => sum + year.totalStudents, 0);
        return Array.from(map.values()).map(row => ({
            ...row,
            participationRate: Math.round((enrolled ? row.participants / enrolled * 100 : 0) * 10) / 10,
            averageHours: Math.round((row.participants ? row.totalHours / row.participants : 0) * 10) / 10,
            value: Math.round((metric === 'participantRate'
                ? (enrolled ? row.participants / enrolled * 100 : 0)
                : (row.participants ? row.totalHours / row.participants : 0)) * 10) / 10
        })).sort((a, b) => a.order - b.order);
    }, [data, metric, totalsByYear]);
    const suffix = metric === 'participantRate' ? '%' : ' hrs';
    const chartTitle = metric === 'participantRate'
        ? 'Participation Rates in GEAR UP Student Services'
        : 'Average Hours Spent in GEAR UP Student Services';
    const chartSubtitle = filter.schoolYears.length
        ? filter.schoolYears.map(year => `${year}-${year + 1}`).join(', ')
        : 'Current School Year';
    const comparisonYears = useMemo(() =>
        Array.from(new Set(data.map(item => item.year))).sort((a, b) => b - a), [data]);
    const comparisonRows = useMemo(() => {
        const serviceRows = new Map<string, any>();
        data.forEach(item => {
            const row = serviceRows.get(item.studentServiceName) || {
                name: item.studentServiceName,
                order: item.studentServiceDisplayOrder,
            };
            row[`participationRate${item.year}`] = item.participantRate;
            row[`averageHours${item.year}`] = item.averageHours;
            serviceRows.set(item.studentServiceName, row);
        });
        return Array.from(serviceRows.values()).sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));
    }, [data]);
    const getComparisonYearColor = (year: number) =>
        getDashboardDistributionColor(comparisonYears.indexOf(year));

    const handleExport = () => chartView === 'pie' || chartView === 'fullPie'
        ? chartRef.current?.instance.exportTo('Student_Services', 'png')
        : cartesianChartRef.current?.instance.exportTo('Student_Services', 'png');

    return <DashboardCard title="Annual Services" onExport={chartView === 'comparisonBar' ? undefined : handleExport}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <StudentServiceFilterControls showMetric={chartView !== 'comparisonBar' && chartView !== 'serviceComparison'} />
                <DashboardChartViewSelect dashboard="studentServices" value={chartView} onChange={setChartView} />
            </Box>
            {loading ? <Skeleton variant="rounded" height={400} /> : error ? <Typography color="error" align="center">{error}</Typography> :
            rows.length === 0 ? <Typography align="center">No student-service data available</Typography> :
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>{chartView === 'comparisonBar'
            ? <StudentServiceComparisonChart rows={rows} subtitle={chartSubtitle} />
            : chartView === 'serviceComparison' ? <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {comparisonYears.length > 1 && <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                    {comparisonYears.map(year => <Box key={year} sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
                        <Box sx={{ width: 12, height: 12, bgcolor: getComparisonYearColor(year), borderRadius: .5 }} />
                        <Typography variant="caption">{year}-{year + 1}</Typography>
                    </Box>)}
                </Box>}
                <Chart ref={cartesianChartRef} dataSource={comparisonRows} height="100%" rotated>
                <ChartTitle text="Student Service Comparison" subtitle={chartSubtitle}><ChartFont size={18} weight={600} /></ChartTitle>
                <ValueAxis name="participationAxis" position="left" title="Participation Rate (%)" />
                <ValueAxis name="hoursAxis" position="right" title="Average Hours" />
                {comparisonYears.flatMap(year => [
                    <ChartSeries key={`participation-${year}`} name={`${year}-${year + 1} Participation Rate`}
                        type="bar" argumentField="name" valueField={`participationRate${year}`}
                        axis="participationAxis" color={lighten(getComparisonYearColor(year), .35)} />,
                    <ChartSeries key={`hours-${year}`} name={`${year}-${year + 1} Average Hours`}
                        type="bar" argumentField="name" valueField={`averageHours${year}`}
                        axis="hoursAxis" color={getComparisonYearColor(year)} />
                ])}
                <ChartLegend visible={false} />
                <ChartTooltip enabled customizeTooltip={({ seriesName, valueText }) => ({
                    text: `${seriesName}: ${valueText}${seriesName?.includes('Participation Rate') ? '%' : ' hours'}`
                })} />
                </Chart>
            </Box>
            : chartView === 'pie' || chartView === 'fullPie' ? <PieChart ref={chartRef} dataSource={rows} height="100%" type={chartView === 'fullPie' ? 'pie' : 'doughnut'} palette={DASHBOARD_DISTRIBUTION_COLORS}>
                <PieTitle text={chartTitle} subtitle={chartSubtitle}><Font size={18} weight={600} /></PieTitle>
                <Series argumentField="name" valueField="value"><Label visible position="outside"
                    customizeText={({ argumentText, valueText }) => `${argumentText}\n${valueText}${suffix}`}><Font size={12} /><Connector visible /></Label></Series>
                <Legend visible={false} /><Tooltip enabled customizeTooltip={({ argumentText, valueText }) => ({ text: `${argumentText}: ${valueText}${suffix}` })} />
            </PieChart> : <Chart ref={cartesianChartRef} dataSource={rows} height="100%" rotated={chartView === 'horizontalBar'} palette={DASHBOARD_DISTRIBUTION_COLORS} customizePoint={(args: any) => ({ color: getDashboardDistributionColor(args.index) })}>
                <ChartTitle text={chartTitle} subtitle={chartSubtitle}><ChartFont size={18} weight={600} /></ChartTitle>
                <ValueAxis title={metric === 'participantRate' ? 'Participation Rate (%)' : 'Average Hours'} />
                <ChartSeries type="bar" argumentField="name" valueField="value">
                    <ChartLabel visible customizeText={({ valueText }) => `${valueText}${suffix}`} />
                </ChartSeries>
                <ChartLegend visible={false} />
                <ChartTooltip enabled customizeTooltip={({ argumentText, valueText }) => ({ text: `${argumentText}: ${valueText}${suffix}` })} />
            </Chart>}</Box>}
        </Box>
    </DashboardCard>;
};
